/**
 * API Health Monitor Component
 * Dedicated dashboard for Route53 health checks with real-time status,
 * response times, uptime SLA, and incident tracking.
 * Executive-grade dashboard using shared monitoring design system.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  MonitorHeart as MonitorHeartIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import { MonitoringChart } from '../charts/ChartComponents';
import { Bar } from 'react-chartjs-2';
import {
  getMonitoringColors,
  mapStatus,
  monoValueSx,
  getChartTheme,
  criticalPulse,
  serviceRowSx,
} from './monitoringDesignSystem';
import {
  MonitoringPageContainer,
  MonitoringHeroBanner,
  MonitoringKPICard,
  MonitoringChartCard,
  MonitoringDonutGauge,
  AutoRefreshIndicator,
} from './MonitoringComponents';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { HOUR_PRESETS, HourToken } from '../common/timeRange';

// ---------------------------------------------------------------------------
// Interfaces (preserved)
// ---------------------------------------------------------------------------

interface HealthCheck {
  id: string;
  name: string;
  fqdn: string;
  resourcePath?: string;
  port?: number;
  type?: string;
  status: 'healthy' | 'unhealthy';
  responseTime: number;
  uptime24h: number;
  lastChecked: string;
  region?: string;
}

interface UptimeData {
  aggregated: Record<string, number | null>;
  byCheck: Record<string, { name: string; uptimes: Record<string, number | null> }>;
}

interface Incident {
  id: string;
  health_check_id: string;
  health_check_name: string;
  endpoint_url: string;
  status: string;
  severity: string;
  started_at: string;
  resolved_at?: string;
  duration_minutes?: number;
  acknowledged_at?: string;
  acknowledged_by?: string;
  acknowledgment_notes?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ApiHealthMonitor: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);
  const ct = getChartTheme(mode);
  const dispatch = useDispatch<AppDispatch>();

  // --- State (preserved) ---
  const [loading, setLoading] = useState(true);
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [uptimeData, setUptimeData] = useState<UptimeData | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [activeIncidents, setActiveIncidents] = useState<Incident[]>([]);
  // Hour-scale window: the SAME pill control as every other screen, over this
  // screen's own vocabulary. `resolveRange()` speaks calendar days only, so the
  // token is handed to the existing fetch logic untouched.
  const [timeRange, setTimeRange] = useTimeRangeParam<HourToken>(
    { type: '24h' },
    { presets: HOUR_PRESETS }
  );
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [acknowledgeDialog, setAcknowledgeDialog] = useState<{ open: boolean; incidentId: string }>({ open: false, incidentId: '' });
  const [acknowledgeNotes, setAcknowledgeNotes] = useState('');

  // --- Data fetching (preserved) ---
  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, historyRes, uptimeRes, incidentsRes, activeRes] = await Promise.all([
        apiService.getApiHealthDashboard(),
        apiService.getApiHealthHistory(timeRange.type),
        apiService.getApiUptime(),
        apiService.getApiIncidents(),
        apiService.getApiActiveIncidents(),
      ]);

      if (dashboardRes?.data?.healthChecks) setHealthChecks(dashboardRes.data.healthChecks);
      if (historyRes?.data) setChartData(historyRes.data);
      if (uptimeRes?.data) setUptimeData(uptimeRes.data);
      if (incidentsRes?.data) setIncidents(incidentsRes.data);
      if (activeRes?.data) setActiveIncidents(activeRes.data);
    } catch (error) {
      console.error('Error fetching API health data:', error);
      dispatch(addNotification({ type: 'error', message: 'Failed to fetch API health data' }));
    } finally {
      setLoading(false);
    }
  }, [timeRange, dispatch]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // --- Handlers (preserved) ---
  const handleAcknowledge = async () => {
    try {
      await apiService.acknowledgeApiIncident(acknowledgeDialog.incidentId, acknowledgeNotes);
      dispatch(addNotification({ type: 'success', message: 'Incident acknowledged' }));
      setAcknowledgeDialog({ open: false, incidentId: '' });
      setAcknowledgeNotes('');
      fetchData();
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: 'Failed to acknowledge incident' }));
    }
  };

  // --- Utility functions (preserved) ---
  const getUptimeColor = (value: number | null) => {
    if (value === null) return c.textSecondary;
    if (value >= 99.9) return c.healthy;
    if (value >= 99.0) return c.warning;
    return c.critical;
  };

  const getResponseTimeColor = (ms: number) => {
    if (ms < 200) return c.healthy;
    if (ms < 500) return c.warning;
    return c.critical;
  };

  // --- Computed ---
  const healthyCount = healthChecks.filter((c) => c.status === 'healthy').length;
  const unhealthyCount = healthChecks.filter((c) => c.status === 'unhealthy').length;
  const avgResponseTime = healthChecks.length > 0
    ? Math.round(healthChecks.reduce((sum, c) => sum + c.responseTime, 0) / healthChecks.length)
    : 0;

  const overallHealth = mapStatus(unhealthyCount > 0 ? 'unhealthy' : 'healthy');
  const bannerLabel = overallHealth === 'healthy' ? 'ALL APIs OPERATIONAL' : 'API HEALTH DEGRADED';
  const bannerSummary = overallHealth === 'healthy'
    ? `${healthyCount}/${healthChecks.length} health checks passing${avgResponseTime > 0 ? ` · Avg response: ${avgResponseTime}ms` : ''}`
    : `${unhealthyCount} of ${healthChecks.length} health checks failing`;

  if (loading) {
    return (
      <MonitoringPageContainer>
        <LinearProgress />
        <Typography sx={{ mt: 2, color: c.textSecondary }}>Loading API Health Monitor...</Typography>
      </MonitoringPageContainer>
    );
  }

  return (
    <MonitoringPageContainer>
      {/* Active Incidents Banner */}
      {activeIncidents.length > 0 && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            ...criticalPulse,
            animation: 'criticalPulse 2s infinite',
            bgcolor: c.criticalBg,
            border: `1px solid ${c.critical}`,
          }}
        >
          <AlertTitle>P0 ALERT: {activeIncidents.length} API health check(s) DOWN</AlertTitle>
          {activeIncidents.map((inc) => (
            <Box key={inc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <ErrorIcon fontSize="small" />
              <Typography variant="body2">
                {inc.health_check_name} — down since {new Date(inc.started_at).toLocaleString()}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setAcknowledgeDialog({ open: true, incidentId: inc.id })}
              >
                Acknowledge
              </Button>
            </Box>
          ))}
        </Alert>
      )}

      {/* Hero Banner */}
      <MonitoringHeroBanner
        status={overallHealth}
        title={bannerLabel}
        subtitle={bannerSummary}
        actions={
          <>
            <TimeRangeFilter
              value={timeRange}
              onChange={setTimeRange}
              presets={HOUR_PRESETS}
              hideCustom
            />
            <AutoRefreshIndicator
              autoRefresh={autoRefresh}
              onToggle={() => setAutoRefresh(!autoRefresh)}
              onRefresh={fetchData}
            />
          </>
        }
      />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Total Checks"
            value={healthChecks.length}
            accentColor={c.textPrimary}
            icon={<MonitorHeartIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Healthy"
            value={healthyCount}
            accentColor={c.healthy}
            icon={<CheckCircleIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Unhealthy"
            value={unhealthyCount}
            accentColor={unhealthyCount > 0 ? c.critical : c.textMuted}
            icon={<ErrorIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Avg Response"
            value={`${avgResponseTime}ms`}
            accentColor={getResponseTimeColor(avgResponseTime)}
            icon={<SpeedIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
      </Grid>

      {/* Latency Bar Chart + Health Score Donut */}
      {healthChecks.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <MonitoringChartCard title="Endpoint Latency" badge="live">
              <Box sx={{ height: 280 }}>
                <Bar
                  data={{
                    labels: healthChecks.map((hc) => hc.name),
                    datasets: [{
                      label: 'Response Time (ms)',
                      data: healthChecks.map((hc) => hc.responseTime),
                      backgroundColor: healthChecks.map((hc) =>
                        hc.responseTime < 200 ? `${c.healthy}B3` :
                        hc.responseTime < 500 ? `${c.warning}B3` :
                        `${c.critical}B3`
                      ),
                      borderColor: healthChecks.map((hc) =>
                        hc.responseTime < 200 ? c.healthy :
                        hc.responseTime < 500 ? c.warning :
                        c.critical
                      ),
                      borderWidth: 1,
                      borderRadius: 4,
                    }],
                  }}
                  options={{
                    indexAxis: 'y' as const,
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: ct.tooltipBg,
                        titleColor: ct.tooltipTitleColor,
                        bodyColor: ct.tooltipBodyColor,
                        borderColor: ct.tooltipBorderColor,
                        borderWidth: 1,
                        callbacks: {
                          label: (ctx) => `${ctx.parsed.x}ms`,
                        },
                      },
                    },
                    scales: {
                      x: {
                        title: { display: true, text: 'Response Time (ms)', color: ct.tickColor },
                        grid: { color: ct.gridColor },
                        ticks: { color: ct.tickColor },
                      },
                      y: {
                        grid: { display: false },
                        ticks: { color: c.textPrimary, font: { weight: 'bold' as const } },
                      },
                    },
                  }}
                />
              </Box>
            </MonitoringChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <MonitoringChartCard title="Health Score">
              <MonitoringDonutGauge
                data={[
                  { label: 'Healthy', value: healthyCount, color: c.healthy },
                  { label: 'Unhealthy', value: unhealthyCount || 0, color: c.critical },
                ]}
                centerValue={`${healthChecks.length > 0 ? Math.round((healthyCount / healthChecks.length) * 100) : 0}%`}
                centerLabel=""
              />
              <Typography
                variant="body2"
                sx={{ color: c.textSecondary, mt: 1.5, fontWeight: 600, textAlign: 'center' }}
              >
                {healthyCount} of {healthChecks.length} endpoints healthy
              </Typography>
            </MonitoringChartCard>
          </Grid>
        </Grid>
      )}

      {/* Service Health Table */}
      <Box sx={{ mb: 3 }}>
        <MonitoringChartCard title="Service Health">
          {healthChecks.length === 0 ? (
            <Alert severity="info">
              No Route53 health checks found. Health checks are configured in AWS Route53 and monitor API endpoint availability.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, color: c.textSecondary }}></TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Service Name</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Endpoint</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Status</TableCell>
                    <TableCell align="right" sx={{ color: c.textSecondary }}>Response Time</TableCell>
                    <TableCell align="right" sx={{ color: c.textSecondary }}>24h Uptime</TableCell>
                    <TableCell sx={{ color: c.textSecondary }}>Last Checked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {healthChecks.map((check) => (
                    <TableRow
                      key={check.id}
                      sx={serviceRowSx(mapStatus(check.status), mode)}
                    >
                      <TableCell sx={{ pl: 2 }}>
                        {check.status === 'healthy' ? (
                          <CheckCircleIcon sx={{ color: c.healthy, fontSize: 22 }} />
                        ) : (
                          <ErrorIcon sx={{ color: c.critical, fontSize: 22 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: c.textPrimary }}>{check.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: '"Fira Code", monospace', fontSize: '0.8rem', color: c.textSecondary }}>
                          {check.fqdn}{check.resourcePath || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={check.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                          color={check.status === 'healthy' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ ...monoValueSx, color: getResponseTimeColor(check.responseTime) }}>
                          {check.responseTime}ms
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ ...monoValueSx, color: getUptimeColor(check.uptime24h) }}>
                          {check.uptime24h != null ? `${check.uptime24h}%` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: c.textMuted }}>
                          {new Date(check.lastChecked).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MonitoringChartCard>
      </Box>

      {/* Response Time Chart */}
      {chartData && chartData.labels?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <MonitoringChartCard title="Response Time" badge="realtime">
            <MonitoringChart
              data={chartData}
              title="Response Time (ms)"
              height={350}
              type="line"
            />
          </MonitoringChartCard>
        </Box>
      )}

      {/* Uptime SLA */}
      {uptimeData && (
        <Box sx={{ mb: 3 }}>
          <MonitoringChartCard title="Uptime SLA">
            <Grid container spacing={3} sx={{ mb: 2 }}>
              {['24h', '7d', '30d', '90d'].map((period) => (
                <Grid item xs={6} md={3} key={period}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      border: `1px solid ${c.cardBorder}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: c.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                    >
                      {period.toUpperCase()}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ ...monoValueSx, color: getUptimeColor(uptimeData.aggregated[period]) }}
                    >
                      {uptimeData.aggregated[period] !== null ? `${uptimeData.aggregated[period]}%` : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: c.textSecondary }}>Health Check</TableCell>
                    <TableCell align="center" sx={{ color: c.textSecondary }}>24h</TableCell>
                    <TableCell align="center" sx={{ color: c.textSecondary }}>7d</TableCell>
                    <TableCell align="center" sx={{ color: c.textSecondary }}>30d</TableCell>
                    <TableCell align="center" sx={{ color: c.textSecondary }}>90d</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(uptimeData.byCheck).map(([id, check]) => (
                    <TableRow key={id}>
                      <TableCell sx={{ color: c.textPrimary }}>{check.name}</TableCell>
                      {['24h', '7d', '30d', '90d'].map((period) => (
                        <TableCell align="center" key={period}>
                          <Typography
                            variant="body2"
                            sx={{ ...monoValueSx, color: getUptimeColor(check.uptimes[period]) }}
                          >
                            {check.uptimes[period] !== null ? `${check.uptimes[period]}%` : 'N/A'}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MonitoringChartCard>
        </Box>
      )}

      {/* Incident History */}
      <MonitoringChartCard title="Incident History">
        {incidents.length === 0 ? (
          <Alert severity="success" sx={{ bgcolor: c.healthyBg }}>No incidents recorded.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: c.textSecondary }}>Time</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Health Check</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Duration</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: c.textSecondary }}>Acknowledged</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidents.map((inc) => (
                  <TableRow key={inc.id}>
                    <TableCell sx={{ color: c.textPrimary }}>{new Date(inc.started_at).toLocaleString()}</TableCell>
                    <TableCell sx={{ color: c.textPrimary }}>{inc.health_check_name}</TableCell>
                    <TableCell sx={{ color: c.textPrimary }}>
                      {inc.duration_minutes != null
                        ? `${Math.round(inc.duration_minutes)} min`
                        : 'Ongoing'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inc.status}
                        color={
                          inc.status === 'resolved' ? 'success' :
                          inc.status === 'acknowledged' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {inc.acknowledged_at ? (
                        <Tooltip title={inc.acknowledgment_notes || ''}>
                          <Typography variant="body2" sx={{ color: c.textPrimary }}>
                            {new Date(inc.acknowledged_at).toLocaleString()}
                          </Typography>
                        </Tooltip>
                      ) : inc.status === 'active' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setAcknowledgeDialog({ open: true, incidentId: inc.id })}
                        >
                          Acknowledge
                        </Button>
                      ) : (
                        <Typography variant="body2" sx={{ color: c.textMuted }}>-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MonitoringChartCard>

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeDialog.open} onClose={() => setAcknowledgeDialog({ open: false, incidentId: '' })}>
        <DialogTitle>Acknowledge Incident</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            value={acknowledgeNotes}
            onChange={(e) => setAcknowledgeNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcknowledgeDialog({ open: false, incidentId: '' })}>Cancel</Button>
          <Button onClick={handleAcknowledge} variant="contained" color="warning">Acknowledge</Button>
        </DialogActions>
      </Dialog>
    </MonitoringPageContainer>
  );
};

export default ApiHealthMonitor;
